import React, { useState, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Lock, Plus, Minus, Check, Clock, PhoneCall, AlertTriangle, Pause, Snowflake, Layers, LogOut } from 'lucide-react';
import BottomTabBar from './BottomTabBar';

/**
 * ContagemScreen
 * Ecrã de contagem para operações de contagem (is_contagem: true)
 * Adaptado do layout Samidel para web
 */
export default function ContagemScreen({
  producaoData,
  lote,
  operacao,
  tempoDecorrido = 0,
  onFechar,
  onParcial,
  onContagemChange,
  onBack,
  onNavigate,
  onChamarGestor,
  onPause,
  onRetomar,
  onCongelar,
  onSairSemContar,
  isPaused = false,
  initialCount = 0,
  quantidadeJaProduzida = 0,
  user,
  tipoContagem = 'unitaria', // 'unitaria' | 'fim'
}) {
  const [count, setCount] = useState(initialCount);
  const [inputFim, setInputFim] = useState(initialCount > 0 ? String(initialCount) : ''); // Para tipo 'fim'
  const [anomalias, setAnomalias] = useState(0); // Anomalias inline
  const lastTap = useRef(null);

  const totalPecas = lote?.quantidade_ajustada || lote?.quantidade || 0;
  const disponivel = Math.max(0, totalPecas - quantidadeJaProduzida);
  const percentComplete = disponivel > 0 ? Math.min(100, Math.round((count / disponivel) * 100)) : (count > 0 ? 100 : 0);
  const isFim = tipoContagem === 'fim';

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const isContagemEspecial = operacao?.is_contagem === true;

  const addPieces = useCallback((amount) => {
    setCount(prev => {
      const limit = disponivel > 0 ? disponivel : totalPecas;
      const next = isContagemEspecial ? prev + amount : Math.min(limit, prev + amount);
      onContagemChange && onContagemChange(next);
      return next;
    });
    // Haptic feedback via vibration API (where available)
    if (navigator.vibrate) navigator.vibrate(30);
  }, [onContagemChange, disponivel, totalPecas, isContagemEspecial]);

  const removePiece = useCallback(() => {
    setCount(prev => {
      const next = Math.max(0, prev - 1);
      onContagemChange && onContagemChange(next);
      return next;
    });
  }, [onContagemChange]);

  const handleFecharProducao = () => {
    onFechar && onFechar(count, anomalias);
  };

  return (
    <div className="flex flex-col h-full min-h-screen overflow-hidden select-none">
      {/* Header — fundo verde escuro, mais compacto */}
      <div
        className="flex-shrink-0 px-5 pb-3 pt-3"
        style={{ background: '#0B3B30' }}
      >
        {/* Linha superior: Congelar (esquerda) + Nome operador + Chamar Gestor + Pausar (direita) */}
        <div className="flex items-center justify-between mb-2">
          {/* Congelar — canto superior esquerdo */}
          {onCongelar ? (
            <button
              onClick={onCongelar}
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(147,197,253,0.25)', border: '1.5px solid rgba(147,197,253,0.5)' }}
              data-testid="btn-congelar-contagem"
              title="Congelar operação"
            >
              <Snowflake className="h-5 w-5" style={{ color: '#93C5FD' }} />
            </button>
          ) : <div className="w-12" />}

          <p className="text-[10px] uppercase tracking-wider text-white/60 flex-1 text-center">
            {user?.nome_completo || user?.nome || user?.username || 'Operador'}
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={onChamarGestor}
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(239,68,68,0.2)' }}
              data-testid="btn-chamar-gestor-contagem"
              title="Chamar Gestor"
            >
              <PhoneCall className="h-5 w-5" style={{ color: '#FCA5A5' }} />
            </button>
            {/* Pausar — canto superior direito */}
            {onPause && (
              <button
                onClick={onPause}
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(251,191,36,0.25)', border: '1.5px solid rgba(251,191,36,0.5)' }}
                data-testid="btn-pausar-contagem"
                title="Pausar"
              >
                <Pause className="h-5 w-5" style={{ color: '#FDE68A' }} />
              </button>
            )}
          </div>
        </div>

        {/* OF + Operação + Detalhes */}
        <div className="flex items-center justify-center mb-1">
          <div className="text-center">
            <p className="text-[10px] font-bold tracking-widest uppercase text-white/60">
              OF {producaoData?.of?.numero_of || producaoData?.of_id?.slice(-6) || '—'}
              {producaoData?.of?.cliente ? ` · ${producaoData.of.cliente}` : ''}
            </p>
            <p className="text-sm font-extrabold text-white leading-tight mt-0.5">
              {operacao?.codigo} {operacao?.nome}
            </p>
            {(producaoData?.of?.artigo || producaoData?.of?.modelo) && (
              <p className="text-[10px] text-white/50 mt-0.5">
                {producaoData?.of?.artigo || ''}
                {producaoData?.of?.modelo ? ` · ${producaoData.of.modelo}` : ''}
                {producaoData?.of?.marca ? ` · ${producaoData.of.marca}` : ''}
              </p>
            )}
          </div>
        </div>

        {/* Lote info + Quantidades — layout visual proeminente */}
        <div className="flex items-center justify-center gap-3 mb-1">
          <span className="text-white/60 text-xs font-medium">
            Lote {lote?.codigo_lote || `L${lote?.numero_lote}`}
          </span>
          <span className="text-white/30">·</span>
          <span className="text-white/60 text-xs font-medium">
            {lote?.categoria_nome || '—'}
          </span>
        </div>

        {/* Info quantidades: 3 blocos visuais grandes */}
        {quantidadeJaProduzida > 0 && (
          <div className="flex items-stretch justify-center gap-2 mb-3 px-2">
            <div className="flex-1 rounded-xl py-2 px-3 text-center" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}>
              <p className="text-[9px] font-bold uppercase tracking-wider text-white/50 mb-0.5">Qt Lote</p>
              <p className="text-lg font-black text-white/80">{totalPecas}</p>
            </div>
            <div className="flex-1 rounded-xl py-2 px-3 text-center" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}>
              <p className="text-[9px] font-bold uppercase tracking-wider text-white/50 mb-0.5">Já feitas</p>
              <p className="text-lg font-black text-white/80">{quantidadeJaProduzida}</p>
            </div>
            <div className="flex-1 rounded-xl py-2 px-3 text-center" style={{ background: 'rgba(34,197,94,0.15)', border: '1.5px solid rgba(34,197,94,0.4)' }}>
              <p className="text-[9px] font-bold uppercase tracking-wider" style={{ color: 'rgba(134,239,172,0.8)' }}>Disponível</p>
              <p className="text-xl font-black" style={{ color: '#22c55e' }}>{disponivel}</p>
            </div>
          </div>
        )}

        {/* Contador principal — mais compacto */}
        <div className="text-center mb-2">
          <div className="mb-1">
            <span className="font-black text-white" style={{ fontSize: '60px', lineHeight: 1 }}>
              {count}
            </span>
            <span className="text-white/50 font-semibold" style={{ fontSize: '28px' }}>
              /{disponivel > 0 ? disponivel : totalPecas}
            </span>
          </div>

          {/* Progress bar */}
          <div className="w-full h-2 rounded-full overflow-hidden mb-1" style={{ background: 'rgba(255,255,255,0.2)' }}>
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                width: `${percentComplete}%`,
                background: percentComplete >= 100 ? '#22c55e' : '#ffffff',
              }}
            />
          </div>

          <p className="text-white/70 text-xs font-semibold">{percentComplete}% concluído</p>
        </div>

        {/* Timer — compacto */}
        <div className="flex items-center justify-center gap-2">
          <Clock className="h-4 w-4" style={{ color: '#F59E0B' }} />
          <span className="font-extrabold font-mono tabular-nums" style={{ fontSize: '22px', color: '#F59E0B', letterSpacing: '2px' }}>
            {formatTime(tempoDecorrido)}
          </span>
        </div>
      </div>

      {/* Área de botões — fundo azul claro arredondado no topo */}
      <div
        className="flex-1 flex flex-col px-5 pt-5 pb-6 overflow-y-auto"
        style={{
          background: '#DBEAFE',
          borderTopLeftRadius: '32px',
          borderTopRightRadius: '32px',
          marginTop: '-2px',
        }}
      >
        {/* Indicação de PAUSA + botão Retomar */}
        {isPaused && (
          <div className="mb-5">
            <button
              onClick={onRetomar}
              className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-2xl text-white font-extrabold text-lg transition-all active:scale-95 animate-pulse"
              style={{ background: '#F59E0B', boxShadow: '0 0 20px rgba(245,158,11,0.4)' }}
              data-testid="btn-retomar-contagem"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              Retomar
            </button>
            <p className="text-center text-sm font-bold mt-2" style={{ color: '#92400E' }}>
              Operação em pausa
            </p>
          </div>
        )}

        {/* Botão Fechar Produção */}
        <button
          onClick={handleFecharProducao}
          className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-2xl mb-5 text-white font-extrabold text-lg transition-opacity active:opacity-80 hover:opacity-90"
          style={{ background: '#DC2626' }}
          data-testid="btn-fechar-producao"
        >
          <Lock className="h-6 w-6" />
          <span>Fechar produção</span>
        </button>

        {isFim ? (
          /* ── Modo "Contagem no Fim" — input simples ────────── */
          <div className="flex flex-col items-center gap-4">
            <p className="text-center font-semibold text-stone-600 text-sm">
              Insira a quantidade total produzida
            </p>
            <div
              className="w-full rounded-3xl bg-white border-4 border-blue-500 p-6 flex flex-col items-center"
              style={{ boxShadow: '0 8px 24px rgba(59,130,246,0.3)' }}
            >
              <input
                type="number"
                min="0"
                max={999999}
                value={inputFim}
                onChange={(e) => {
                  const limit = disponivel > 0 ? disponivel : totalPecas;
                  const val = Math.min(parseInt(e.target.value) || 0, limit);
                  setInputFim(val > 0 ? val.toString() : e.target.value);
                  if (val > 0) {
                    setCount(val);
                    onContagemChange && onContagemChange(val);
                  }
                }}
                placeholder={`0–${disponivel > 0 ? disponivel : totalPecas}`}
                className="w-full text-center text-5xl font-black outline-none bg-transparent text-blue-600"
                style={{ lineHeight: 1.2 }}
                data-testid="input-contagem-fim"
              />
              <p className="text-blue-400 font-semibold mt-2">de {disponivel > 0 ? disponivel : totalPecas} peças</p>
            </div>

            {/* Botões rápidos de preenchimento */}
            <div className="flex gap-3 w-full">
              <button
                onClick={() => { const t = disponivel > 0 ? disponivel : totalPecas; setInputFim(String(t)); setCount(t); }}
                className="flex-1 py-3 rounded-2xl font-extrabold text-sm border-2 border-blue-500 bg-white text-blue-500"
                data-testid="btn-total-lote"
              >
                Total ({disponivel > 0 ? disponivel : totalPecas})
              </button>
              <button
                onClick={() => { const h = Math.floor((disponivel > 0 ? disponivel : totalPecas) / 2); setInputFim(String(h)); setCount(h); }}
                className="flex-1 py-3 rounded-2xl font-extrabold text-sm border-2 border-stone-300 bg-white text-stone-600"
              >
                Metade
              </button>
            </div>

            {/* Campo inline de anomalias */}
            <div className="w-full rounded-2xl border-2 p-3 mt-1" style={{ background: '#FFFBEB', borderColor: '#F59E0B' }}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4" style={{ color: '#B45309' }} />
                <span className="font-bold text-sm" style={{ color: '#B45309' }}>Anomalias (opcional)</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setAnomalias(prev => Math.max(0, prev - 1))}
                  disabled={anomalias <= 0}
                  className="w-9 h-9 rounded-full flex items-center justify-center bg-white border-2 disabled:opacity-30"
                  style={{ borderColor: '#F59E0B' }}
                >
                  <Minus className="h-4 w-4" style={{ color: '#B45309' }} />
                </button>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  max={999999}
                  value={anomalias}
                  onChange={e => {
                    const v = parseInt(e.target.value) || 0;
                    setAnomalias(Math.max(0, Math.min(v, totalPecas)));
                  }}
                  className="flex-1 text-center font-black text-xl py-1.5 rounded-xl border-2 bg-white outline-none"
                  style={{ borderColor: '#F59E0B', color: '#000' }}
                  data-testid="input-anomalias-contagem"
                />
                <button
                  onClick={() => setAnomalias(prev => Math.min(prev + 1, totalPecas))}
                  disabled={anomalias >= totalPecas}
                  className="w-9 h-9 rounded-full flex items-center justify-center bg-white border-2 disabled:opacity-30"
                  style={{ borderColor: '#F59E0B' }}
                >
                  <Plus className="h-4 w-4" style={{ color: '#B45309' }} />
                </button>
              </div>
            </div>

            {onParcial && (
              <button
                onClick={() => onParcial(count, anomalias)}
                className="w-full flex items-center justify-center gap-2 py-3.5 mt-2 rounded-2xl font-extrabold text-base transition-all active:scale-95"
                style={{ background: '#1E3A5F', color: '#FFFFFF' }}
                data-testid="btn-parcial-fim"
              >
                <Layers className="h-5 w-5" />
                <span>Registar Parcial</span>
              </button>
            )}

            {/* Sair sem contar — botão discreto */}
            {onSairSemContar && (
              <button
                onClick={onSairSemContar}
                className="w-full flex items-center justify-center gap-2 py-2.5 mt-3 text-stone-400 font-medium text-sm transition-opacity active:opacity-60 hover:text-stone-600"
                data-testid="btn-sair-sem-contar-fim"
              >
                <LogOut className="h-4 w-4" />
                <span>Sair sem contar</span>
              </button>
            )}
          </div>
        ) : (
          /* ── Modo "Contagem Unitária" — botões +1/+2/+5/+10 ── */
          <>
            {/* Quick buttons */}
            <div className="flex gap-3 justify-center mb-6">
              {[2, 5, 10].map(n => (
                <button
                  key={n}
                  onClick={() => addPieces(n)}
                  className="flex-1 py-3.5 rounded-2xl font-extrabold text-xl border-2 border-blue-500 bg-white text-blue-500 transition-all active:scale-95 hover:bg-blue-50"
                  data-testid={`btn-add-${n}`}
                >
                  +{n}
                </button>
              ))}
            </div>

            {/* Botão principal +1 */}
            <button
              onClick={() => addPieces(1)}
              className="w-full flex flex-col items-center justify-center rounded-3xl bg-white border-4 border-blue-500 mb-4 transition-all active:scale-98 hover:bg-blue-50"
              style={{ height: '120px', boxShadow: '0 8px 24px rgba(59,130,246,0.3)' }}
              data-testid="btn-add-1"
            >
              <Plus className="text-blue-500" style={{ width: '56px', height: '56px', strokeWidth: 1.5 }} />
              <span className="text-blue-500 font-extrabold text-lg mt-1">Mais uma unidade</span>
            </button>

            {/* Botão remover */}
            <button
              onClick={removePiece}
              disabled={count === 0}
              className="w-full flex items-center justify-center gap-2 py-3 text-stone-600 disabled:opacity-40 transition-opacity active:opacity-60"
              data-testid="btn-remove-1"
            >
              <Minus className="h-5 w-5" />
              <span className="font-semibold text-base">Remover última</span>
            </button>

            {/* Botão parcial — destaque no rodapé */}
            {onParcial && (
              <button
                onClick={() => onParcial(count, anomalias)}
                className="w-full flex items-center justify-center gap-2 py-3.5 mt-3 rounded-2xl font-extrabold text-base transition-all active:scale-95"
                style={{ background: '#1E3A5F', color: '#FFFFFF' }}
                data-testid="btn-parcial-unitaria"
              >
                <Layers className="h-5 w-5" />
                <span>Registar Parcial</span>
              </button>
            )}

            {/* Sair sem contar — botão discreto */}
            {onSairSemContar && (
              <button
                onClick={onSairSemContar}
                className="w-full flex items-center justify-center gap-2 py-2.5 mt-3 text-stone-400 font-medium text-sm transition-opacity active:opacity-60 hover:text-stone-600"
                data-testid="btn-sair-sem-contar-unitaria"
              >
                <LogOut className="h-4 w-4" />
                <span>Sair sem contar</span>
              </button>
            )}
          </>
        )}
      </div>

      {/* BottomTabBar */}
      <BottomTabBar
        activeTab="contagem"
        onChange={(tab) => {
          if (tab === 'timer' || tab === 'contagem') return;
          if (onNavigate) {
            onNavigate(tab);
          } else if (tab === 'inicio') {
            onBack && onBack();
          }
        }}
        emProducao={true}
      />

    </div>
  );
}
